/* istanbul ignore file */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectStationFilter, selectUserSession } from "../slices/userSession/userSessionSlice";
import {
  addBidAdminEvent,
  addBidEmpEvent,
  addNotificationEvent,
  addScheduleAdminEvent,
  addFreezeEvent,
  addTaedAdminEvent,
  addTorAdminEvent,
  addTorEmpEvent,
  addTradeAdminEvent,
  addTradeEmpEvent,
  clearAllWebsocketData,
  clearStationWebSocketData,
} from "../slices/webSocketEvent/webSocketEventSlice";
import {
  WEBSOCKET_CLOSE_REASON_CHANGING_STATIONS,
  WEBSOCKET_CLOSE_REASON_LOGGING_OUT,
  WEBSOCKET_MSG_TYPE,
} from "../util/constants";
import { getWebSocketClient, setConnectInterval } from "../util/webSocketClient";

const WebSocketHandler = () => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const dispatch = useDispatch();

  useEffect(() => {
    initWebSocketConnection(userSession.eid, userSession.isAdmin, stationFilter);
    dispatch(clearStationWebSocketData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter]);

  const initWebSocketConnection = (eid, isAdmin, station) => {
    console.debug("initWebSocketConnection");
    console.debug("eid: ", eid, isAdmin, station);
    let webSocketClient = getWebSocketClient(eid, isAdmin, station);
    webSocketClient.onmessage = (message) => {
      const newEvent = JSON.parse(message.data);
      switch (newEvent.messageType) {
        case WEBSOCKET_MSG_TYPE.TRADE_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          if (isAdmin) {
            if (isAdminEvent(newEvent, station)) {
              dispatch(addTradeAdminEvent(newEvent));
            }
          }
          if (isEmployeeEvent(newEvent, eid)) {
            dispatch(addTradeEmpEvent(newEvent));
          }
          break;
        case WEBSOCKET_MSG_TYPE.TOR_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          if (isAdmin) {
            if (isAdminEvent(newEvent, station)) {
              dispatch(addTorAdminEvent(newEvent));
            }
          }
          if (isEmployeeEvent(newEvent, eid)) {
            dispatch(addTorEmpEvent(newEvent));
          }
          break;
        case WEBSOCKET_MSG_TYPE.TAED_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          dispatch(addTaedAdminEvent(newEvent));
          break;
        case WEBSOCKET_MSG_TYPE.SCHEDULE_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          dispatch(addScheduleAdminEvent(newEvent));
          break;
        case WEBSOCKET_MSG_TYPE.FREEZE_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          dispatch(addFreezeEvent(newEvent));
          break;
        case WEBSOCKET_MSG_TYPE.BID_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          dispatch(addBidAdminEvent(newEvent));
          break;
        case WEBSOCKET_MSG_TYPE.BID_CHOICE_EVENT:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          dispatch(addBidEmpEvent(newEvent));
          break;
        case WEBSOCKET_MSG_TYPE.NOTIFICATION:
          console.debug("Received a " + newEvent.messageType + " event: ", newEvent);
          dispatch(addNotificationEvent(newEvent));
          break;
        default:
          console.error("Uh oh, websocket type unknown: ", newEvent.messageType);
          break;
      }
    };
    webSocketClient.onclose = (closeEvent) => {
      if (
        closeEvent.reason !== WEBSOCKET_CLOSE_REASON_LOGGING_OUT &&
        closeEvent.reason !== WEBSOCKET_CLOSE_REASON_CHANGING_STATIONS &&
        closeEvent.wasClean
      ) {
        console.debug("WebSocket Client Disconnected");
        reconnectWebSocket(eid, isAdmin, station);
      } else {
        dispatch(clearAllWebsocketData());
      }
    };
    webSocketClient.onerror = (err) => {
      console.error("websocket error...");
      console.error(err);
      reconnectWebSocket(eid, isAdmin, station);
    };
  };

  const reconnectWebSocket = (eid, isAdmin, station) => {
    console.debug("Attempting to reconnect...");
    setConnectInterval(
      setTimeout(function () {
        initWebSocketConnection(eid, isAdmin, station);
      }, 1000)
    );
  };

  const isAdminEvent = (newEvent, station) => {
    return newEvent.station === station;
  };

  const isEmployeeEvent = (newEvent, eid) => {
    return isEmployeeTradeEvent(newEvent, eid) || isEmployeeTOREvent(newEvent, eid);
  };

  const isEmployeeTradeEvent = (newEvent, eid) => {
    return newEvent.requestedEmployeeId === eid || newEvent.offeredEmployeeId === eid;
  };

  const isEmployeeTOREvent = (newEvent, eid) => {
    return newEvent.requestingEmployeeId === eid;
  };

  return null;
};

export default WebSocketHandler;
